import { AccessibilityHelper } from "../GlobalAssets/js/accessibilityHelper";
import gsap from "gsap";
import { ScrollToPlugin } from "gsap/ScrollToPlugin";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { PartnersListingsFilter } from "../PartnersListingsFilter/PartnersListingsFilter";

class PartnersListings {
  constructor(component) {
    this.refs = {
      allPartnersList: '.js-all-partners-list',
      listingSection: '.js-listing-section',
      navItem: '.js-nav-item',
      navLink: '.js-nav-link',
      filter: '.js-partners-listings-filter',
      partnerGroup: '.js-partner-group',
      partnerListingTitle: '.js-partner-listing-title',
      partnersTagsData: '#partners-tags-data',
    };

    this.dom = {
      $component: component,
      $allPartnersList: component.querySelector(this.refs.allPartnersList),
      $listingSections: component.querySelectorAll(this.refs.listingSection),
      $navItems: component.querySelectorAll(this.refs.navItem),
      $navLinks: component.querySelectorAll(this.refs.navLink),
      $filterModules: component.querySelectorAll(this.refs.filter),
      $partnerGroups: component.querySelectorAll(this.refs.partnerGroup),
      $partnersTagsData: component.querySelector(this.refs.partnersTagsData),
    };

    this.state = {
      innerWidth: null,
      view: null,
      initialAllListingHeight: null,
      filterInstances: {},
      activeFilters: [],
      filterAnimation: null,
      partnersTagsData: null,
      totalVisibleItems: 0,
    };

    this.mount();

    this.init();
  }

  mount() {
    const { dom, state } = this;
    console.log('mounting PartnersListings');
    window.addEventListener('resize', this.handleResize.bind(this));

    dom.$filterModules.forEach(filter => {
      const filterObject = new PartnersListingsFilter(filter);
      const location = filter.dataset.location;
      state.filterInstances[location] = filterObject;

      filter.addEventListener(
        'filters:change',
        this.handleFilterEvent.bind(this)
      );

      filter.addEventListener(
        'filters:reset',
        this.handleFilterReset.bind(this)
      );
    });

    dom.$navLinks.forEach($link => {
      $link.addEventListener('click', this.handleNavLinkClick.bind(this));
    });
  }

  init() {
    const { dom, state } = this;
    // Initial setup if needed
    this.setView();
    state.initialAllListingHeight = dom.$allPartnersList.clientHeight;
    this.setAllPartnersListHeight();

    gsap.registerPlugin(ScrollToPlugin, ScrollTrigger);

    dom.$listingSections.forEach(($listing) => {
      ScrollTrigger.create({
        trigger: $listing,
        start: 'top top+=70',
        end: 'bottom top+=70',
        once: false,
        onToggle: (self) => {
          this.setActiveNavItem($listing.id);
        }
      });
    });

    this.getPartnersTagsData();
    this.syncFilterUI();
  }

  getPartnersTagsData() {
    const { dom, state } = this;
    const partnersTagsDataEl = dom.$partnersTagsData;
    const partnersTagsData = partnersTagsDataEl ? JSON.parse(partnersTagsDataEl.textContent) : {};
    state.partnersTagsData = partnersTagsData;
  }

  setActiveNavItem(targetId) {
    const { dom, state } = this;

    const $activeNavItem = dom.$component.querySelector(`button[data-anchor-id="${targetId}"]`).closest(this.refs.navItem);

    // Remove active class and aria-current from all nav items
    dom.$navItems.forEach($item => {
      $item.classList.remove('is-active');
    });
    dom.$navLinks.forEach($link => {
      $link.setAttribute('aria-current', 'false');
    });
    // Add active class and aria-current to current nav item
    if ($activeNavItem) {
      $activeNavItem.classList.add('is-active');
      const $activeLink = $activeNavItem.querySelector(this.refs.navLink);
      if ($activeLink) $activeLink.setAttribute('aria-current', 'true');
    }
  }

  handleFilterEvent(e) {
    const { value, checked } = e.detail;
    const { state } = this;

    if (checked) {
      if (!state.activeFilters.includes(value)) {
        state.activeFilters.push(value);
      }
    } else {
      state.activeFilters = state.activeFilters.filter(v => v !== value);
    }

    this.animateFilterChange();
  }

  animateFilterChange() {
    const { dom, state } = this;
    const list = dom.$allPartnersList;

    // Kill any in-progress animation immediately
    if (state.filterAnimation) {
      state.filterAnimation.kill();
    }

    state.filterAnimation = gsap.timeline({ defaults: { ease: 'power1.out' } })
      .to(list, {
        opacity: 0,
        duration: 0.2,
        pointerEvents: 'none',
        onComplete: () => {
          this.applyFilters();
          this.syncFilterUI();
          this.updateEmptyState();
        }
      })
      .to(list, {
        opacity: 1,
        duration: 0.25,
        pointerEvents: 'auto'
      });
  }

  handleNavLinkClick(e) {
    const { dom, state } = this;

    const $btn = e.currentTarget;
    const targetId = $btn.dataset.anchorId;
    const offsetY = 67;

    const $target = document.getElementById(targetId);

    gsap.to(window, {
      duration: .8,
      scrollTo: { y: `#${targetId}`, offsetY, autoKill: false },
      ease: "Power1.easeInOut",
      onComplete: () => {
        if ($target) $target.focus({ preventScroll: true });
      }
    });
  }

  setAllPartnersListHeight() {
    const { dom, state } = this;

    let newHeight = Math.ceil(state.initialAllListingHeight / 2);

    if (state.view === 'desktop') {
      newHeight = Math.ceil(state.initialAllListingHeight / 4);
    } else if (state.view === 'tablet') {
      newHeight = Math.ceil(state.initialAllListingHeight / 3);
    }

    newHeight += 80; // Adding extra space for better UX

    // console.log('Setting all partners list height to:', state.view, state.initialAllListingHeight, newHeight);

    dom.$allPartnersList.style.maxHeight = `${newHeight}px`;
  }

  setView() {
    const { state } = this;

    state.innerWidth = window.innerWidth;

    if (state.innerWidth < 768) {
      state.view = 'mobile';
      return;
    }

    if (state.innerWidth >= 768 && state.innerWidth < 1200) {
      state.view = 'tablet';
      return;
    }

    state.view = 'desktop';
  }

  handleResize() {
    const { state } = this;
    if (state.innerWidth === window.innerWidth) return;

    this.setView();
    console.log('Resized - new view:', state.view, state.innerWidth);
    this.setAllPartnersListHeight();
  }

  applyFilters() {
    const { state, dom, refs } = this;

    let totalVisibleItems = 0;
    state.totalVisibleItems = 0;

    // If no filters are active, show everything
    if (state.activeFilters.length === 0) {
      dom.$partnerGroups.forEach($partnerGroup => {
        $partnerGroup.classList.remove('hide');
        $partnerGroup.querySelectorAll(refs.partnerListingTitle).forEach($title => {
          totalVisibleItems++;
          $title.classList.remove('hide');
        });
      });
      state.totalVisibleItems = totalVisibleItems;
      return;
    }

    // ------------------------------------------
    // STEP 1: Build mapping of active filters to groups
    // ------------------------------------------
    // Use state.partnersTagsData to know which filter belongs to which group
    const activeFiltersByGroup = {};

    state.activeFilters.forEach(filter => {
      let found = false;

      // Check grouped_tags
      state.partnersTagsData.grouped_tags.forEach(groupData => {
        const groupSlug = groupData.group.slug; // assuming slug is unique key
        const groupTermSlugs = groupData.terms.map(t => t.slug);

        if (groupTermSlugs.includes(filter)) {
          if (!activeFiltersByGroup[groupSlug]) activeFiltersByGroup[groupSlug] = [];
          activeFiltersByGroup[groupSlug].push(filter);
          found = true;
        }
      });

      // Check other_tags
      if (!found) {
        const otherSlugs = state.partnersTagsData.other_tags.map(t => t.slug);
        if (otherSlugs.includes(filter)) {
          if (!activeFiltersByGroup.other) activeFiltersByGroup.other = [];
          activeFiltersByGroup.other.push(filter);
        }
      }
    });

    // ------------------------------------------
    // STEP 2: Apply filtering (AND across groups, OR within group)
    // ------------------------------------------
    dom.$partnerGroups.forEach($partnerGroup => {
      const $titles = $partnerGroup.querySelectorAll(refs.partnerListingTitle);
      let groupHasVisible = false;

      $titles.forEach($title => {
        let passes = true;

        for (const [filterGroup, filters] of Object.entries(activeFiltersByGroup)) {
          const itemTags = ($title.dataset[filterGroup] || '').split(' ');

          // If filter group has no active filters OR filter array is empty → no match exists → hide item
          if (!filters.length) {
            passes = false;
            break;
          }

          // OR within group
          const matchesFilterGroup = filters.some(f => itemTags.includes(f));
          if (!matchesFilterGroup) {
            passes = false;
            break;
          }
        }

        $title.classList.toggle('hide', !passes);
        if (passes) {
          groupHasVisible = true;
          totalVisibleItems++;
        }
      });

      $partnerGroup.classList.toggle('hide', !groupHasVisible);
      state.totalVisibleItems = totalVisibleItems;
    });
  }

  syncFilterUI() {
    const { state, dom } = this;

    const count = state.activeFilters.length;

    dom.$filterModules.forEach(filter => {
      const location = filter.dataset.location;

      state.filterInstances[location].updateState({
        count: count,
        activeFilters: state.activeFilters,
      });
    });
  }

  handleFilterReset(e) {
    e.preventDefault();

    const { state } = this;

    state.activeFilters = [];
    this.animateFilterChange();
  }

  updateEmptyState() {
    const { dom, state } = this;

    dom.$component.classList.toggle('no-filtered-results', state.totalVisibleItems === 0);
  }
}

const $partnersListings = document.querySelector('.js-partners-listings');
if ($partnersListings) new PartnersListings($partnersListings);
